<script lang="ts">
    import { acim } from "@utils/acim"
    import type { SearchResults } from "@circleofa/acim-engine"
    import { createEventDispatcher } from "svelte"
    import { navigatorOpen } from "./Navigator.svelte"
    import Loader from "@components/utilities/Loader.svelte"
    import { fade } from "svelte/transition"
    import { goto } from "$app/navigation"
    import { selectedTabID } from "@store/pageTracker"
    import ResultsBlockView from "../../routes/search/ResultsBlockView.svelte"
    import Icon from "@components/material/Icon.svelte"
    import {
        RedrawEventDispatcher,
        contextScrollContainerClass,
    } from "@utils/highlights/directive-logic/highlights-directive"
    import { ContextMenuViewportSelection } from "@utils/highlights/directive-logic/highlight-listener-permissions"
    import Button from "@components/button/Button.svelte"

    const dispatch = createEventDispatcher()
    export let search = ""
    let limit = 10
    const redrawEventDispatcher = new RedrawEventDispatcher()
    let resultsWidth = 0
    $: redrawEventDispatcher.resize(resultsWidth)

    /**
     * Call this from the parent to make a navigation call to the first hit
     */
    export const navigateToFirstSearch = () => {
        // No hits
        if (!results || !results.results.length) return
        dispatch("navigate")
        goto("/book/" + results.results[0].path)
    }

    // $: {search; process()}
    $: {
        search
        debounceProcess()
    }

    function debounceProcess() {
        clearTimeout(loaderNoResultsWait)
        results = null
        showLoader = true
        // debounceFn("qs", process, 50)
        process()
    }

    let showLoader = false
    let results: SearchResults | null = null

    let error: string | null = null
    async function getResults(str: string) {
        return str.trim().length ? acim.defaultSearch(str) : null
    }

    let loaderNoResultsWait: ReturnType<typeof setTimeout>
    async function process() {
        clearTimeout(loaderNoResultsWait)

        error = null
        results = null
        try {
            results = await getResults(search)

            // There are results
            if (results?.results?.length) {
                showLoader = false
            } else {
                loaderNoResultsWait = setTimeout(() => {
                    showLoader = false
                }, 1000)
            }
        } catch (e) {
            showLoader = false
            console.warn(e)
            error = "" + e
        }
    }
</script>

{#if error}
    <p class="centerDescription">Uh oh! Something went wrong! {error}</p>
{:else if showLoader}
    <div class="loadingContainer">
        <Loader message="" />
    </div>
{:else if results && results.results.length}
    <!-- <span class="hits">
        {results.results.length} hits
        </span> -->
    <div
        bind:clientWidth={resultsWidth}
        class="searchResults flex flex-col pb-2 mx-4 mb-4 gap-4 relative {contextScrollContainerClass}"
        style="--context-menu-top-offset: 50px"
        transition:fade|global
    >
        <center class="text-sm"
            >Showing {Math.min(limit, results.results.length)} of {results.results.length}</center
        >
        <ResultsBlockView
            {redrawEventDispatcher}
            viewportSelection={ContextMenuViewportSelection.SCROLL_CONTAINER}
            on:navigate
            forceInstantFirstPaint={true}
            searchResults={results.sortByRelevance}
            {limit}
            disableSelection={true}
            directOpen={true}
            visitedResetKey={$selectedTabID}
            class={"shadow-none p-2 dark:bg-neutral-900 dark:hover:bg-neutral-800 data-[visited=true]:bg-neutral-100 data-[visited=true]:dark:bg-neutral-700"}
        />

        <center class="text-sm">
            Showing {Math.min(limit, results.results.length)} of {results.results.length}
        </center>

        {#if limit < results.results.length}
            <!-- There's more results -->
            <Button small class="mx-auto" color="white" icon="plus" go={() => (limit = limit + 10)}>
                See more
            </Button>
        {/if}
        <center class="rounded-lg mt-3 relative p-2 m-2 bg-yellow-500 bg-opacity-10">
            For more fine-tuning, try the
            <a on:click={() => dispatch("navigate")} class="link underline" href="/search"
                >advanced search</a
            >.
        </center>
    </div>
{:else}
    <p class="centerDescription">
        Nothing matched! Maybe an <a
            class="link underline"
            on:click={() => ($navigatorOpen = false)}
            href={"/search"}>advanced search</a
        > can help you find what you're looking for?
    </p>
{/if}

<style>
    .loadingContainer {
        position: absolute;
        min-height: 50%;
        width: 100%;
        height: 250px;
    }
</style>
